/**
 * One-shot recovery policy for read failures a second attempt can heal.
 *
 * Two independent arms, one link, one retry budget each.
 *
 * **Arm 1 — backend dead-socket eviction (SE-6450).** SE-5608 gave the backend
 * a client-side `query_timeout`: the first query written to a pool socket that
 * RDS Proxy silently closed is killed after 26s with a `"Query read timeout"`
 * GraphQL error — and the pool discards the dead socket, so the IMMEDIATE retry
 * lands on a live connection and succeeds in milliseconds (measured signature:
 * FAIL @26.5s → OK <1s). That design expects the caller to retry; without one,
 * every first-interaction-after-idle on a quiet environment surfaces as an
 * error state to the user (and fails the dev→staging promote's e2e).
 *
 * **Arm 2 — aborted batch (SE-7338).** `BatchHttpLink` coalesces up to ten
 * concurrent queries into ONE POST behind ONE `AbortController` armed at
 * `BATCH_REQUEST_TIMEOUT_MS`. One slow member takes every co-batched query down
 * with it, and a consumer that fires once at mount with no transport-error
 * recovery is then finished for the whole session: `data` stays `undefined`
 * with `loading` false and no error anywhere the surface reads, so the UI
 * presents a starved list as an authoritative empty one. Measured instance:
 * `ListMyKanbanBoards {first:100}` co-batched with six unrelated operations,
 * `status=-1`, never retried — the board strip stayed empty all session and
 * every new board was auto-named `"Pipeline 1"` off `boards.length === 0`.
 *
 * The retry for arm 2 is deliberately sent **unbatched**. Re-issuing it into
 * another batch would restore the exact coupling that killed it — the slowest
 * of N still decides its fate. Going out alone puts it on the plain link's
 * shorter single-op budget, where it stands or falls on its own latency. That
 * is the same reasoning `AuthMe` applies permanently with
 * `context: { batch: false }` (SE-6233), applied here only on the recovery
 * path so the happy path keeps the full batching win.
 *
 * Both arms stay narrow: retry ONCE, QUERIES only (idempotent — mutations must
 * never be re-fired on a timeout the server may have executed), and only on a
 * signature that a retry can actually heal. A user-initiated abort
 * (`resetStore`, a superseded typeahead) is NOT retried; neither is an HTTP
 * error, a GraphQL error, or any other network failure.
 * @module providers/readTimeoutRetry
 */

import { type Operation } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import type { GraphQLFormattedError } from "graphql";

import { isQueryOperation } from "@/providers/batchTerminalLink";
import { isTimeoutError } from "@/utils/formatError";

/** The backend's SE-5608 client-side eviction message, matched exactly. */
const READ_TIMEOUT_MESSAGE = "Query read timeout";

/** Context flag marking an operation already retried once. */
export const READ_TIMEOUT_RETRIED_KEY = "readTimeoutRetried";

/**
 * Context flag marking an operation already retried once for a client-side
 * transport timeout. Separate from {@link READ_TIMEOUT_RETRIED_KEY} so the two
 * arms cannot silently consume each other's single retry.
 */
export const TRANSPORT_TIMEOUT_RETRIED_KEY = "transportTimeoutRetried";

/**
 * Decides whether a failed operation should be retried once for a
 * dead-socket read-timeout eviction.
 * @param graphQLErrors - Errors from the failed response
 * @param isQuery - Whether the operation is a query (idempotent)
 * @param alreadyRetried - Whether this operation already used its retry
 * @returns True when the single retry should be forwarded
 */
export function shouldRetryReadTimeout(
  graphQLErrors: readonly GraphQLFormattedError[] | undefined,
  isQuery: boolean,
  alreadyRetried: boolean
): boolean {
  if (!isQuery || alreadyRetried) return false;
  if (graphQLErrors == null || graphQLErrors.length === 0) return false;
  return graphQLErrors.every(error => error.message === READ_TIMEOUT_MESSAGE);
}

/**
 * Applies {@link shouldRetryReadTimeout} to a live Apollo operation.
 * @param graphQLErrors - Errors from the failed response
 * @param operation - The failed Apollo operation
 * @returns True when the caller should `forward(operation)` once
 */
export function shouldRetryOperation(
  graphQLErrors: readonly GraphQLFormattedError[] | undefined,
  operation: Operation
): boolean {
  return shouldRetryReadTimeout(
    graphQLErrors,
    isQueryOperation(operation),
    operation.getContext()[READ_TIMEOUT_RETRIED_KEY] === true
  );
}

/**
 * Decides whether a failed operation should be retried once because the CLIENT
 * gave up on the transport — `createTimeoutFetch` firing its `AbortController`
 * at the link's budget (SE-7338).
 * @remarks
 * `isTimeoutError` matches only the `name: "TimeoutError"` that
 * `createTimeoutFetch` raises from its own timer. React Native does not
 * reliably carry `signal.reason`, so that wrapper tracks its `didTimeout` flag
 * itself and re-labels the rejection; an abort from any other source keeps its
 * `AbortError` name and is excluded here. That distinction is what keeps an
 * intentional `resetStore()` cancellation (SE-4940) from being re-fired.
 * @param networkError - The transport-level error from the failed attempt
 * @param isQuery - Whether the operation is a query (idempotent)
 * @param alreadyRetried - Whether this operation already used its retry
 * @returns True when the single retry should be forwarded
 */
export function shouldRetryTransportTimeout(
  networkError: unknown,
  isQuery: boolean,
  alreadyRetried: boolean
): boolean {
  if (!isQuery || alreadyRetried) return false;
  return isTimeoutError(networkError);
}

/**
 * Applies {@link shouldRetryTransportTimeout} to a live Apollo operation.
 * @param networkError - The transport-level error from the failed attempt
 * @param operation - The failed Apollo operation
 * @returns True when the caller should `forward(operation)` once, unbatched
 */
export function shouldRetryTransportTimeoutOperation(
  networkError: unknown,
  operation: Operation
): boolean {
  return shouldRetryTransportTimeout(
    networkError,
    isQueryOperation(operation),
    operation.getContext()[TRANSPORT_TIMEOUT_RETRIED_KEY] === true
  );
}

/**
 * Builds the Apollo link enforcing this policy. Composed BEFORE the error
 * link so an eviction is healed transparently and never reaches the
 * refresh/error handling below it.
 * @returns The retry link
 */
export function createReadTimeoutRetryLink() {
  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (shouldRetryOperation(graphQLErrors, operation)) {
      operation.setContext({ [READ_TIMEOUT_RETRIED_KEY]: true });
      return forward(operation);
    }
    if (shouldRetryTransportTimeoutOperation(networkError, operation)) {
      // `batch: false` is the point of the retry, not a detail of it: the
      // first attempt died of a co-batched neighbour, so the second must not
      // acquire new ones. `shouldBatchOperation` reads this key off the
      // context, and setContext merges, so the auth/POV headers written
      // downstream on the way back out are untouched.
      operation.setContext({
        [TRANSPORT_TIMEOUT_RETRIED_KEY]: true,
        batch: false,
      });
      return forward(operation);
    }
    return undefined;
  });
}
